import { ShaderBuilder } from "../shader_builder/shader_builder.js";
import { InstanceBatch } from "../core/batching/instance_batch.js";
import { InstanceController } from "../core/controllers/instance_controller.js";
import { BlendMode, AttributeSize, WebGLTextureSource } from "../core/types/webgl_types.js";
import { Vector2 } from "../../common/vector2.js";
import { WebGLUIRenderLayer } from "../core/runtime/webgl_ui_render_layer.js";
import { WebGLUIRuntime } from "../webgl_ui_runtime.js";
import { WebGLTextureUtils } from "../utils/webgl_texture_utils.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { ImgID } from "../../image_loader/img_id.js";

export class GlintInstanceController extends InstanceController
{
  private batch_glint: WebGLUIGlint;

  constructor( batch: WebGLUIGlint, stable_id?: number | number[] )
  {
    super( batch, stable_id );
    this.batch_glint = batch;
  }

  public setTransformAndOpacity( pos: Vector2, angle: number, size: Vector2, opacity: number ): void
  {
    const ids = Array.isArray( ( this as any ).stable_ids )
      ? ( this as any ).stable_ids
      : [ ( this as any ).stable_ids[0] ];

    for ( const id of ids )
      this.batch_glint.updateGlintTransform( id, pos, angle, size, opacity );
  }
}

export interface IWebGLGlintEntry
{
  position: Vector2;
  size_physical: Vector2;
  canvas: HTMLCanvasElement;
  glint_angle_deg?: number;
  glint_width?: number;
  duration: number;
  intensity?: number;
  color?: { r: number; g: number; b: number };
  repeat_delay?: number;
  on_finish?: () => void;
}

export class WebGLUIGlint extends InstanceBatch
{
  private static readonly MAX_INSTANCES: number         = 5;
  private static readonly VERTICES_PER_INSTANCE: number = 6;

  public static readonly DEFAULT_GLINT_ANGLE_DEG: number = 135.0;
  public static readonly DEFAULT_GLINT_WIDTH: number     = 0.35;
  public static readonly DEFAULT_INTENSITY: number       = 1.2;
  public static readonly DEFAULT_REPEAT_DELAY: number    = 0;
  public static readonly DIRTY_RECT_PAD: number          = 10;
  private static readonly DIRTY_RECT_EXPAND_FACTOR: number = 1.5;

  private callbacks: ( Function | null )[]        = [];
  private glint_durations: number[]               = [];
  private glint_delays: number[]                  = [];
  private sources: ( WebGLTextureSource | null )[] = [];
  private ui_layer: WebGLUIRenderLayer;

  constructor( ui_layer: WebGLUIRenderLayer )
  {
    super( {
      max_instances: WebGLUIGlint.MAX_INSTANCES,
      primitive_type: WebGL2RenderingContext.TRIANGLES,
      vertices_per_instance: WebGLUIGlint.VERTICES_PER_INSTANCE,
      blend_mode: BlendMode.Normal,
      attribute_layout: [
        { location: 0, size: AttributeSize.Vec2,  name: "a_position" },
        { location: 1, size: AttributeSize.Vec2,  name: "a_size" },
        { location: 2, size: AttributeSize.Vec4,  name: "a_uv_rect" },
        { location: 3, size: AttributeSize.Float, name: "a_time" },
        { location: 4, size: AttributeSize.Float, name: "a_layer" },
        { location: 5, size: AttributeSize.Float, name: "a_width" },
        { location: 6, size: AttributeSize.Float, name: "a_intensity" },
        { location: 7, size: AttributeSize.Float, name: "a_opacity" },
        { location: 8, size: AttributeSize.Vec2,  name: "a_glint_rot" },
        { location: 9, size: AttributeSize.Vec3,  name: "a_color" },
      ]
    } );

    this.ui_layer = ui_layer;

    this.buildShader( new ShaderBuilder()
      .withUniform( "sampler2DArray", "u_atlas" )
      .withUniform( "sampler2D", "u_glint_tex" )
      .withVarying( "vec2", "v_local01" )
      .withVarying( "vec2", "v_uv" )
      .withVarying( "vec2", "v_glint_rot", "flat" )
      .withVarying( "vec3", "v_color", "flat" )
      .withVarying( "float", "v_time", "flat" )
      .withVarying( "float", "v_layer", "flat" )
      .withVarying( "float", "v_width", "flat" )
      .withVarying( "float", "v_intensity", "flat" )
      .withVarying( "float", "v_opacity", "flat" )
      .withVertexLogic( `
        float t = clamp( a_time, 0.0, 1.0 );
        
        position = a_position;
        size     = a_size;

        v_local01   = uv;
        v_uv        = a_uv_rect.xy + uv * a_uv_rect.zw;
        v_time      = t;
        v_layer     = a_layer;
        v_width     = a_width;
        v_intensity = a_intensity;
        v_opacity   = a_opacity;
        v_glint_rot = a_glint_rot;
        v_color     = a_color;
      ` )
      .withFragmentLogic( `
        vec4 crown_mask = texture( u_atlas, vec3( v_uv, v_layer ) );

        vec2 uv_local = v_local01 - vec2( 0.5 );
        vec2 rot_uv = vec2(
          uv_local.x * v_glint_rot.x - uv_local.y * v_glint_rot.y,
          uv_local.x * v_glint_rot.y + uv_local.y * v_glint_rot.x
        );

        float bound_pad = 0.75 * ( abs( v_glint_rot.x ) + abs( v_glint_rot.y ) );
        float soft      = max( 1e-5, v_width );
        float bound     = bound_pad + soft;
        float shine_pos = mix( -bound, bound, v_time );
        
        float d_line    = abs( rot_uv.y - shine_pos );
        float local_pos = rot_uv.x + 0.5;

        float in_glint      = step( 0.0, local_pos ) * step( local_pos, 1.0 );
        float glint_profile = texture( u_glint_tex, vec2( 0.5, clamp( local_pos, 0.0, 1.0 ) ) ).a * in_glint;
        float band          = 1.0 - smoothstep( 0.0, soft, d_line );

        float shine = glint_profile * band;
        
        // Ignore soft drop shadow:
        float mask = smoothstep( 0.4, 0.6, crown_mask.a );
        float alpha = shine * mask;

        if ( alpha <= 0.001 )
          discard;

        vec3 raw_rgb = crown_mask.rgb / ( crown_mask.a + 1e-6 );
        float spec = pow( shine, 8.0 ) * 0.15 * v_intensity;
        
        vec3 final_rgb = ( raw_rgb * v_intensity + vec3( spec ) * v_color ) * alpha * v_opacity;

        // Output with 0.0 alpha to act as a purely additive blend over 2D canvas in browser
        fragColor = vec4( final_rgb, 0.0 );
      ` )
    );
  }

  protected override onInit( gl: WebGL2RenderingContext ): void
  {
    const tex = WebGLUIRuntime.getMainAtlasTexture();
    if ( tex )
    {
      this.textures.push( {
        unit: 0,
        location: "u_atlas",
        texture: tex,
        target: gl.TEXTURE_2D_ARRAY
      } );
    }

    const glint_img = ImagesHolder.getImage( ImgID.LineGlow1 );
    if ( glint_img )
    {
      const glint_texture = this.createTexture( glint_img, gl );
      this.textures.push( { unit: 1, location: "u_glint_tex", texture: glint_texture } );
    }

    this.callbacks       = new Array( this.max_instances ).fill( null );
    this.glint_durations = new Array( this.max_instances ).fill( 0 );
    this.glint_delays    = new Array( this.max_instances ).fill( 0 );
    this.sources         = new Array( this.max_instances ).fill( null );
  }

  protected override onBeforeRender( gl: WebGL2RenderingContext ): void
  {
    const tex = WebGLUIRuntime.getMainAtlasTexture();
    if ( this.textures.length > 0 && tex )
    {
      this.textures[0].texture = tex;
    }
  }

  public spawn( entry: IWebGLGlintEntry, start_time: number ): GlintInstanceController
  {
    const { w, h } = WebGLTextureUtils.getSourceSize( entry.canvas );
    const alloc    = WebGLUIRuntime.allocateInMainAtlas( entry.canvas, w, h );
    if ( !alloc )
      return new GlintInstanceController( this, -1 );

    const repeat_delay = entry.repeat_delay ?? WebGLUIGlint.DEFAULT_REPEAT_DELAY;
    const is_repeating = repeat_delay > 0;
    const duration     = is_repeating ? 0 : entry.duration;

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
    {
      WebGLUIRuntime.freeInMainAtlas( entry.canvas );
      return new GlintInstanceController( this, -1 );
    }

    this.callbacks[idx] = entry.on_finish ?? null;
    this.sources[idx]   = entry.canvas;

    const fpi      = this.buffer.floats_per_instance;
    const offset   = idx * fpi;
    const gpu_data = this.buffer.getDataArray();

    const glint_angle_deg = entry.glint_angle_deg ?? WebGLUIGlint.DEFAULT_GLINT_ANGLE_DEG;
    const glint_rad       = ( glint_angle_deg * Math.PI ) / 180.0;
    const glint_width     = entry.glint_width ?? WebGLUIGlint.DEFAULT_GLINT_WIDTH;
    const intensity       = entry.intensity ?? WebGLUIGlint.DEFAULT_INTENSITY;
    const color           = entry.color ?? { r: 1.0, g: 1.0, b: 1.0 };

    gpu_data[offset + 0]  = entry.position.x;
    gpu_data[offset + 1]  = entry.position.y;
    gpu_data[offset + 2]  = entry.size_physical.x;
    gpu_data[offset + 3]  = entry.size_physical.y;
    gpu_data[offset + 4]  = alloc.uv.x;
    gpu_data[offset + 5]  = alloc.uv.y;
    gpu_data[offset + 6]  = alloc.uv.z;
    gpu_data[offset + 7]  = alloc.uv.w;
    gpu_data[offset + 8]  = 0.0;
    gpu_data[offset + 9]  = alloc.layer;
    gpu_data[offset + 10] = glint_width;
    gpu_data[offset + 11] = intensity;
    gpu_data[offset + 12] = 1.0;
    gpu_data[offset + 13] = Math.cos( glint_rad );
    gpu_data[offset + 14] = Math.sin( glint_rad );
    gpu_data[offset + 15] = color.r;
    gpu_data[offset + 16] = color.g;
    gpu_data[offset + 17] = color.b;

    this.glint_durations[idx] = entry.duration;
    this.glint_delays[idx]    = repeat_delay;
    this.buffer_dirty         = true;

    const radius = Math.max( entry.size_physical.x, entry.size_physical.y ) * WebGLUIGlint.DIRTY_RECT_EXPAND_FACTOR;
    this.ui_layer.addDirtyRect( entry.position.x - radius, entry.position.y - radius, radius * 2, radius * 2 );

    return new GlintInstanceController( this, this.getStableId( idx ) );
  }

  public updateGlintTransform( stable_id: number, pos: Vector2, angle: number, size: Vector2, opacity: number ): void
  {
    const idx = this.stable_to_idx.get( stable_id );
    if ( idx === undefined || idx === -1 )
      return;

    const fpi      = this.buffer.floats_per_instance;
    const offset   = idx * fpi;
    const gpu_data = this.buffer.getDataArray();

    gpu_data[offset + 0]  = pos.x;
    gpu_data[offset + 1]  = pos.y;
    gpu_data[offset + 2]  = size.x;
    gpu_data[offset + 3]  = size.y;
    gpu_data[offset + 12] = opacity;
    this.buffer_dirty     = true;
  }

  protected override updateInstance( gpu_data: Float32Array, offset: number, _progress: number, now: number, idx: number ): boolean
  {
    const duration = this.glint_durations[idx];
    const delay    = this.glint_delays[idx];

    if ( delay <= 0 )
    {
      const elapsed = now - this.start_times[idx];
      if ( elapsed >= duration )
        return false;
      gpu_data[offset + 8] = Math.min( 1.0, elapsed / duration );
    }
    else
    {
      const total_cycle = duration + delay;
      if ( total_cycle > 0 && now > this.start_times[idx] + total_cycle )
      {
        const elapsed_since_start = now - this.start_times[idx];
        const cycles_passed       = Math.floor( elapsed_since_start / total_cycle );
        this.start_times[idx]    += cycles_passed * total_cycle;
      }

      const elapsed        = now - this.start_times[idx];
      const t              = Math.min( 1.0, elapsed / duration );
      gpu_data[offset + 8] = t;
    }

    const px = gpu_data[offset + 0];
    const py = gpu_data[offset + 1];
    const sx = gpu_data[offset + 2];
    const sy = gpu_data[offset + 3];
    
    const radius = Math.max( sx, sy ) * WebGLUIGlint.DIRTY_RECT_EXPAND_FACTOR;
    this.ui_layer.addDirtyRect( px - radius, py - radius, radius * 2, radius * 2 );

    return true;
  }

  protected onSwapInstance( idx_a: number, idx_b: number ): void
  {
    const temp_cb = this.callbacks[idx_a];
    this.callbacks[idx_a] = this.callbacks[idx_b];
    this.callbacks[idx_b] = temp_cb;

    const temp_dur = this.glint_durations[idx_a];
    this.glint_durations[idx_a] = this.glint_durations[idx_b];
    this.glint_durations[idx_b] = temp_dur;

    const temp_delay = this.glint_delays[idx_a];
    this.glint_delays[idx_a] = this.glint_delays[idx_b];
    this.glint_delays[idx_b] = temp_delay;

    const temp_src = this.sources[idx_a];
    this.sources[idx_a] = this.sources[idx_b];
    this.sources[idx_b] = temp_src;
  }

  protected onInstanceDeath( idx: number ): void
  {
    const cb = this.callbacks[idx];
    if ( cb )
    {
      cb();
      this.callbacks[idx] = null;
    }

    const src = this.sources[idx];
    if ( src )
    {
      WebGLUIRuntime.freeInMainAtlas( src );
      this.sources[idx] = null;
    }
    
    const fpi      = this.buffer.floats_per_instance;
    const offset   = idx * fpi;
    const gpu_data = this.buffer.getDataArray();
    const px       = gpu_data[offset + 0];
    const py       = gpu_data[offset + 1];
    const sx       = gpu_data[offset + 2];
    const sy       = gpu_data[offset + 3];
    const radius   = Math.max( sx, sy ) * WebGLUIGlint.DIRTY_RECT_EXPAND_FACTOR;
    this.ui_layer.addDirtyRect( px - radius, py - radius, radius * 2, radius * 2 );
  }
}
